import React, { useCallback, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { useTabsWithQueryParams } from 'src/app/hooks/use-tabs-with-query-params';
import { useYupFormValidation } from 'src/app/hooks/use-yup-field-validation';
import { TABS } from 'src/app/pages/manufacturing/shared/constants';
import { useCompleteAssembly } from 'src/app/queries/manufacturing/complete-assembly';
import { useUpsertAssembly } from 'src/app/queries/manufacturing/upsert-assembly';
import { routes } from 'src/app/routes';
import { useNotificationActions } from 'src/app/state/notifications';

import { Status } from '../constants';
import { getAreQuantitiesValid, getPackagesExceedingAvailableQuantity, mapFormDataToUpsertPayload } from '../utils';

import { assemblySchema } from './schema';
import { AssemblyDetails } from './tabs/detail';
import { AssemblyDocuments } from './tabs/documents';
import { AssemblyProcessingJob } from './tabs/processing-jobs';
import { useAssemblyFormData } from './use-assembly-form-data';
import { useAssemblyFormStateContext } from './use-form-state';

import type { AssemblyDetailFormProps } from '.';
import type { UpsertAssemblyResponse } from 'src/app/queries/manufacturing/upsert-assembly';

export const useAssemblyForm = ({ formType }: AssemblyDetailFormProps) => {
  const navigate = useNavigate();
  const navigateBackToListPage = () => navigate(routes.manufacturing.assemblies);
  const navigateToAssemblyDetail = (assemblyId: string) =>
    navigate(`${routes.manufacturing.assemblies}/${assemblyId}?tab=details`);

  // Form Data Fetching
  const {
    assemblyData,
    assemblyNotFound,
    integrations: { UseMetrcV2, UseMetrcProcessingJobs, UseBioTrack },
    locationSettings: { defaultCostType },
    dropdownOptions,
    isLoading: isLoadingFormData,
    hasError,
  } = useAssemblyFormData({ formType });
  const isEditing = formType === 'edit';

  const [{ formData, context, isDirty, isReadonly, packagesExceedingAvailable }, dispatch] =
    useAssemblyFormStateContext();
  const showBlockNavigationModal = useLDFlag(LD.CREATE_BROWSER_ROUTER_ROLLOUT, false);

  const canViewProcessingJobs =
    UseMetrcV2 && UseMetrcProcessingJobs && formData.processingJobTypeTemplate.isProcessingJob;

  // Constants
  const tabs = [
    {
      label: TABS.details.label,
      queryParam: TABS.details.queryParam,
      renderTab: () => <AssemblyDetails dropdownOptions={dropdownOptions} />,
    },
    {
      label: TABS.documents.label,
      queryParam: TABS.documents.queryParam,
      renderTab: () => <AssemblyDocuments />,
    },
    ...(canViewProcessingJobs
      ? [
          {
            label: TABS.processingJobs.label,
            queryParam: TABS.processingJobs.queryParam,
            renderTab: () => <AssemblyProcessingJob />,
          },
        ]
      : []),
  ];

  // Hooks
  const { assemblyId } = useParams();
  const notifications = useNotificationActions();
  const { activeTab, setActiveTab } = useTabsWithQueryParams(tabs.map((t) => t.queryParam));

  const { isLoading: isCompletingAssembly } = useCompleteAssembly();
  const { mutate: upsertAssembly, isLoading: isSavingAssembly } = useUpsertAssembly();

  const onValidation = useCallback((errors) => dispatch({ type: 'set-errors', payload: errors }), [dispatch]);

  const { isValid: isFormDataValid } = useYupFormValidation({
    schema: assemblySchema,
    formData,
    onValidation,
    context,
  });

  // Only check qunatities if incomplete. Otherwise inputs have already been validated.
  // This check prevents the following:
  // * Assembly is in progress, the input quantity has already been deducted from available.
  // * The remaining available is less than the input quantity. Performing this check will then return false when quantity availablity has already been handled.
  const areQuantitiesValid =
    formData.assemblyStatusId === Status.INCOMPLETE ? getAreQuantitiesValid(formData.outputs) : true;

  const currentPackagesExceedingAvailable = getPackagesExceedingAvailableQuantity(formData);
  const arePackagesExceedingAvailable = currentPackagesExceedingAvailable.length !== 0;

  useEffect(() => {
    // Check if the number of packages exceeding available has changed
    // This catches cases where packages are added/removed from the exceeding list
    const hasPackageCountChanged = packagesExceedingAvailable.length !== currentPackagesExceedingAvailable.length;

    // Check if the actual packages in the exceeding list have changed
    // This catches cases where the count is the same but different packages are now exceeding
    const hasPackageListChanged = !packagesExceedingAvailable.every((pkg) =>
      currentPackagesExceedingAvailable.includes(pkg)
    );

    // Both checks are needed to catch where there are more packages in current, but the same packages still in existing.
    // ['item1'].every((item) => ['item1', 'item2'].includes(item)); - returns true, when we want false.
    const havePackagesChanged = hasPackageCountChanged || hasPackageListChanged;

    // Only update and notify if the invalid packages have changed
    if (havePackagesChanged) {
      dispatch({ type: 'set-packages-exceeding-available', payload: currentPackagesExceedingAvailable });

      if (currentPackagesExceedingAvailable.length > 0) {
        notifications.alert({
          message: `The following packages exceed their available quantity: ${currentPackagesExceedingAvailable.join(
            ', '
          )}`,
        });
      }
    }
  }, [currentPackagesExceedingAvailable, packagesExceedingAvailable, notifications, dispatch]);

  // Derived state

  const isLoading = isLoadingFormData || isSavingAssembly;
  const isSaving = isSavingAssembly;
  const isValid = isFormDataValid && areQuantitiesValid && !arePackagesExceedingAvailable;

  // Initialize state when editing
  useEffect(() => {
    if (isEditing && !isLoadingFormData && !hasError && assemblyData) {
      dispatch({ type: 'initialize-edit-assembly-state', payload: assemblyData });
    }
  }, [isEditing, isLoadingFormData, hasError, assemblyData, dispatch]);

  const isCompleteButtonDisabled =
    isCompletingAssembly || !(formData.assemblyStatusId === Status.IN_PROGRESS) || isDirty;
  const ignoreOutputFields = isCompleteButtonDisabled;

  // Set form context
  useEffect(() => {
    dispatch({
      type: 'set-context',
      payload: {
        useMetrcV2: UseMetrcV2,
        useBioTrack: UseBioTrack,
        defaultCostType,
        ignoreOutputFields,
      },
    });
  }, [dispatch, UseBioTrack, UseMetrcV2, defaultCostType, ignoreOutputFields]);

  const onSubmit = () => {
    const action = isEditing ? 'updated' : 'created';
    // Mutation Callbacks
    const onSuccess = (data: UpsertAssemblyResponse) => {
      notifications.success({ message: `Assembly has been successfully ${action}` });
      dispatch({ type: 'set-is-dirty', payload: false });
      if (!isEditing) {
        navigateToAssemblyDetail(data.assemblyId);
      }
    };
    const onError = (error) => {
      const message = error?.response?.data?.Message;
      const defaultMessage = `Failed to ${action.slice(0, -1)} Assembly`;
      notifications.error({ message: message || defaultMessage });
    };

    // Call Mutations
    const payload = mapFormDataToUpsertPayload({
      assemblyId: isEditing ? Number(assemblyId) : undefined,
      billOfMaterialsId: formData.billOfMaterialsId ?? 0,
      estimatedStartDate: formData.estimatedStartDate ?? new Date(),
      name: formData.name,
      outputs: formData.outputs,
    });
    upsertAssembly(payload, { onSuccess, onError });
  };

  return {
    activeTab,
    assemblyData,
    assemblyNotFound,
    showBlockNavigationModal,
    setActiveTab,
    dropdownOptions,
    onSubmit,
    navigateBackToListPage,
    tabs,
    isValid,
    isDirty,
    isLoading,
    hasError,
    isSaving,
    isReadonly,
  };
};
